import { useEffect, useState } from 'react'
import { ui } from '../styles/ui'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import heuristicRulesService from '../services/heuristicRulesService'
import { useTranslation } from '../contexts/TranslationContext'

export default function HeuristicRulesAdmin() {
  const [rules, setRules] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage] = useState(25)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    loadRules()
  }, [page])

  function loadRules() {
    setLoading(true)
    heuristicRulesService.list(page, perPage, search).then((res) => {
      const d = res.data
      setRules(d.data || [])
      setTotal(d.total || 0)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  function doSearch(e) {
    e.preventDefault()
    setPage(1)
    loadRules()
  }

  function confirmDelete(id) {
    setDeleteId(id)
  }

  function doDelete() {
    if (!deleteId) return
    heuristicRulesService.remove(deleteId).then(() => {
      setDeleteId(null)
      if (rules.length === 1 && page > 1) {
        setPage((p) => p - 1)
      } else {
        loadRules()
      }
    }).catch(() => setDeleteId(null))
  }

  function confidenceBadge(score) {
    if (score >= 10) return 'bg-green-100 text-green-800'
    if (score >= 5) return 'bg-blue-100 text-blue-800'
    if (score >= 3) return 'bg-yellow-100 text-yellow-800'
    return 'bg-surface-elevated text-muted-foreground'
  }

  return (
    <PageShell>
      <PageHeader
        title={t('Heuristic Rules', 'Heuristic Rules')}
        subtitle={`${total} rules`}
      />

      <form onSubmit={doSearch} className="flex gap-2">
        <input
          className={ui.input}
          placeholder={t('Search by product name...', 'Search by product name...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button type="submit">{t('Search', 'Search')}</Button>
      </form>

      <Card>
        <div className="overflow-x-auto">
          {loading ? (
            <LoadingState />
          ) : (
            <table className={ui.table}>
              <thead className={ui.tableHead}>
                <tr>
                  <th className={ui.tableHeadCell}>{t('Product Name', 'Product Name')}</th>
                  <th className={ui.tableHeadCell}>{t('Category ID')}</th>
                  <th className={ui.tableHeadCell}>{t('Confidence', 'Confidence')}</th>
                  <th className={ui.tableHeadCell}>{t('Usage', 'Usage')}</th>
                  <th className={ui.tableHeadCell}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-muted-foreground">
                      {t('No heuristic rules found', 'No heuristic rules found')}
                    </td>
                  </tr>
                ) : rules.map((rule) => (
                  <tr key={rule.id} className={ui.tableRow}>
                    <td className={ui.tableCell}>
                      <span className="text-sm font-medium text-foreground">{rule.normalizedProductName}</span>
                    </td>
                    <td className={ui.tableCellText}>
                      <code className="text-xs bg-surface-elevated px-1.5 py-0.5 rounded">{rule.categoryId}</code>
                    </td>
                    <td className={ui.tableCell}>
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${confidenceBadge(rule.confidenceScore)}`}>
                        {rule.confidenceScore}
                      </span>
                    </td>
                    <td className={ui.tableCellText}>{rule.usageCount}</td>
                    <td className={ui.tableCell}>
                      <Button size="sm" variant="danger" onClick={() => confirmDelete(rule.id)}>
                        {t('Delete', 'Delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > perPage && (
          <div className="mt-4 flex items-center gap-3">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              {t('Previous')}
            </Button>
            <span className="text-sm text-muted-foreground">{t('Page')} {page} / {Math.ceil(total / perPage)}</span>
            <Button variant="ghost" disabled={page * perPage >= total} onClick={() => setPage((p) => p + 1)}>
              {t('Next')}
            </Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={doDelete}
        title={t('Delete heuristic rule', 'Delete heuristic rule')}
      >
        {t('Are you sure you want to delete this heuristic rule?', 'Are you sure you want to delete this heuristic rule?')}
      </ConfirmDialog>
    </PageShell>
  )
}

